import { useEffect, useState, useCallback, useMemo } from 'react';
import FilterSearch from '../../../components/FilterSearch/FilterSearch';
import { Badge, Button, Spinner, Table } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlSearchParams } from 'use-url-search-params';
import { fetchTOList } from './actions/action';
import DHPagination from '../../../components/Pagination';
import { getTechniciansList } from '../ManageServiceOrders/actions/action';

const ItemList = ({
  onViewDetails,
  transferOrderList,
  isLoading,
  setIsLoading,
}) => {
  const dispatch = useDispatch();
  const [paginationValue, setPaginationValue] = useUrlSearchParams({ page: 0 });
  const [limitValue, setLimitValue] = useUrlSearchParams({ limit: 10 });
  const [toListData, setToListData] = useState([]);
  // State for FilterSearch
  const [searchFilter, setSearchFilter] = useState({ search: '' });
  const [statusFilter, setStatusFilter] = useState({ status: 'All' });
  const [advancedFilter, setAdvancedFilter] = useState({});

  useEffect(() => {
    getTechniciansList({ dispatch });
  }, []);

  const { techniciansList } = useSelector((state) => state.technician);

  const technicianMap = useMemo(() => {
    const map = {};
    const list = techniciansList?.technicians || [];
    if (Array.isArray(list)) {
      list.forEach((tech) => {
        map[tech.emp_id] = tech;
      });
    }
    return map;
  }, [techniciansList]);

  const [isDataLoading, setDataLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    let list = [];
    if (transferOrderList && Array.isArray(transferOrderList)) {
      list = transferOrderList;
    }

    // Group by to_number and take the first occurrence
    const grouped = list.reduce((acc, current) => {
      const existing = acc.find((item) => item.to_number === current.to_number);
      if (!existing) {
        acc.push({ ...current, total_items: 1 });
      } else {
        existing.total_items += 1;
      }
      return acc;
    }, []);

    const sorted = grouped.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
    setTotalRecords(sorted.length);

    const page = Number(paginationValue.page) || 0;
    const limit = Number(limitValue.limit) || 10;
    const paginated = sorted.slice(page * limit, (page + 1) * limit);

    setToListData(paginated);
  }, [transferOrderList, paginationValue.page, limitValue.limit]);

  const getTOList = useCallback(
    async (data) => {
      try {
        setDataLoading(true);
        await fetchTOList({
          params: data,
          dispatch,
        });
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setDataLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    // Only reset to page 0 if filters actually changed to a new state
    // We can omit setPaginationValue from here to avoid loops if the hook isn't stable
    setPaginationValue({ page: 0 });
  }, [searchFilter, statusFilter, advancedFilter, limitValue.limit]);

  useEffect(() => {
    const delay = setTimeout(() => {
      getTOList({
        start: Number(paginationValue.page * limitValue.limit),
        limit: Number(limitValue.limit),
        sort: {
          value: 'id',
          order: 1,
        },
        search_qry: searchFilter?.search,
        product_type: statusFilter?.status,
        advancedFilter,
      });
    }, 300);
    return () => clearTimeout(delay);
  }, [
    paginationValue.page,
    limitValue.limit,
    searchFilter,
    statusFilter,
    advancedFilter,
    getTOList,
  ]);

  return (
    <>
      <FilterSearch
        setAdvancedFilter={setAdvancedFilter}
        setSearchFilter={setSearchFilter}
        setStatusFilter={setStatusFilter}
        filterType="toList"
      />
      <div className="dt-table">
        <div className="table-responsive">
          <Table className="OrderListTable table mb-0">
            <thead>
              <tr className="align-middle">
                <th>SI.No</th>
                <th>TO Details</th>
                <th>Technician Details</th>
                <th>Transfer Details</th>
                <th>Transfer Status</th>
                <th>Created By</th>
                <th>Actions</th>
              </tr>
            </thead>
            {isLoading || isDataLoading ? (
              <tbody>
                {[...Array(2)].map((_, index) => (
                  <tr key={index} className="align-middle">
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-2"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow d-flex flex-column gap-1">
                        <span className="placeholder col-6"></span>
                        <span className="placeholder w-75"></span>
                        <span className="placeholder col-3"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow d-flex flex-column gap-1">
                        <span className="placeholder col-6"></span>
                        <span className="placeholder w-75"></span>
                        <span className="placeholder col-3"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td className="text-center">
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : toListData?.length > 0 ? (
              toListData?.map((item, index) => (
                <tbody
                  key={index}
                  className={`${
                    item?.isAmc && item?.isAmc === '1' ? 'bg-soft-light' : ''
                  }`}
                >
                  <tr className="align-middle">
                    <td>
                      {Number(paginationValue.page) * Number(limitValue.limit) +
                        index +
                        1}
                    </td>
                    <td>
                      <div className="medium">
                        <b>TO Number:</b> {item?.to_number}
                        <br />
                        <b>Total Items:</b>{' '}
                        <span className="badge bg-soft-info text-info">
                          {item?.total_items}
                        </span>
                        <br />
                        <b>Created on:</b>{' '}
                        {item?.created_at
                          ? item.created_at.substring(0, 10)
                          : ''}
                      </div>
                    </td>
                    <td>
                      <div className="medium">
                        <b>Name:</b>{' '}
                        {technicianMap[item?.technician_id]?.technician_name ||
                          'N/A'}
                        <br />
                        <b>Email:</b>{' '}
                        {technicianMap[item?.technician_id]?.email || 'N/A'}
                        <br />
                        <b>Technician ID:</b> {item?.technician_id}
                      </div>
                    </td>
                    <td>
                      <Badge color="light">{item?.location_from}</Badge>{' '}
                      <i className="fa fa-arrow-right me-1"></i>
                      <Badge color="light">{item?.location_to}</Badge>
                    </td>
                    <td>
                      <Badge
                        color={
                          item?.status?.toLowerCase() === 'pending'
                            ? 'warning'
                            : ['completed', 'partially completed'].includes(item?.status?.toLowerCase())
                              ? 'success'
                              : ['grn completed', 'partially grn completed'].includes(item?.status?.toLowerCase())
                                ? 'info'
                                : 'danger'
                        }
                      >
                        {item?.status}
                      </Badge>
                      <br />
                      {item?.status?.toLowerCase() === 'pending' && (
                        <span className="badge bg-soft-info text-info">
                          Waiting For GRN
                        </span>
                      )}
                      {['grn completed', 'partially grn completed'].includes(item?.status?.toLowerCase()) &&
                        Number(item?.technician_approval_status) === 0 && (
                          <span className="badge bg-soft-warning text-warning">
                            Waiting for Technician Approval
                          </span>
                        )}
                      {Number(item?.technician_approval_status) === 1 && item?.proof_of_to && (
                        <a
                          href={item.proof_of_to}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="badge bg-soft-primary text-primary text-decoration-none"
                        >
                          <i className="mdi mdi-file-image-outline me-1"></i>
                          View Document
                        </a>
                      )}
                    </td>
                    <td>
                      {`${item?.created_by_name} - (${item?.created_by})`}
                      <br />
                    </td>
                    <td className="text-center">
                      <a
                        type="button"
                        onClick={() => onViewDetails(item?.to_number)}
                        className="btn btn-sm btn-outline-warning waves-effect waves-light"
                      >
                        <i className="mdi mdi-eye-outline"></i>
                      </a>
                    </td>
                  </tr>
                </tbody>
              ))
            ) : (
              <tbody>
                <tr>
                  <td colSpan={8}>No Records found</td>
                </tr>
              </tbody>
            )}
          </Table>
        </div>

        {totalRecords > 0 ? (
          <DHPagination
            totalPages={Math.ceil(
              totalRecords / Number(limitValue.limit || 10),
            )}
            currentPage={Number(paginationValue.page) || 0}
            setPaginationValue={setPaginationValue}
            setLimitValue={setLimitValue}
          />
        ) : null}
      </div>
    </>
  );
};

export default ItemList;
